import { BaseQueryApi, BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isPast } from 'date-fns';
import queryString from 'query-string';
import { Mutex } from 'async-mutex';

const mutex = new Mutex();

let queryDelay = 0;
let casUrl = 'localhost:8100';
export const setQueryDelay = (delay: string | null | undefined) => {
  if (delay == null) {
    return;
  }
  queryDelay = +delay;
};

export const setCasApiBaseUrl = (casBaseUrl: string) => {
  casUrl = casBaseUrl;
};

const setCommonHeaders = (headers: Headers, accessToken: string, tenantId: string | null, organizationId: string | null) => {
  headers.set('Authorization', `Bearer ${accessToken}`);
  if (tenantId) headers.set('x-tenant-context-id', tenantId);
  if (organizationId) headers.set('x-organization-context-id', organizationId);
};

type ExtraOptions = { isCas?: boolean; skipTenantContext?: boolean };

const dynamicBaseQuery: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError, ExtraOptions> = async (
  args,
  api: BaseQueryApi,
  extraOptions: ExtraOptions,
) => {
  if (queryDelay > 0) {
    console.log(`⚠️ Delaying call to ${api.endpoint} by ${queryDelay}ms`);
    await new Promise((resolve) => setTimeout(resolve, queryDelay));
  }

  return fetchBaseQuery({
    baseUrl: extraOptions?.isCas ? `${window.location.protocol}//${casUrl}` : undefined,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const selectedTenantId = localStorage.getItem('selectedTenantId');
      const selectedOrganizationId = localStorage.getItem('selectedOrganizationId');
      const endpointTenantId = headers.get('x-tenant-context-id');
      const endpointOrganizationId = headers.get('x-organization-context-id');

      if (!headers.has('Authorization') && accessToken && selectedTenantId && refreshToken) {
        const token = api.endpoint === 'postApiAuthRefreshToken' ? refreshToken : accessToken;
        const tenantId = extraOptions?.skipTenantContext ? null : endpointTenantId || selectedTenantId;
        // Allow an endpoint to pin a specific organization context (e.g. acting on a user in
        // another org than the one currently selected). Falls back to the selected org otherwise.
        const organizationId = endpointOrganizationId || selectedOrganizationId;

        if (token) setCommonHeaders(headers, token, tenantId, organizationId);
      }

      return headers;
    },
    paramsSerializer: (params: Record<string, unknown>) => queryString.stringify(params, { arrayFormat: 'none' }),
  })(args, api, extraOptions);
};

/**
 * Parse all fetch request response for server status 401, 500
 * ToDo - Handling also 404 server response
 * @param args
 * @param api
 * @param extraOptions
 */
export const dynamicBaseQueryWithReAuth: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError, ExtraOptions> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await dynamicBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    const exp = localStorage.getItem('exp');
    const expNumber = Number(exp);

    if (
      args.url?.includes('/api/auth/refresh-token') ||
      ((!refreshToken || !exp || isNaN(expNumber)) && !window.location.pathname.endsWith('/login'))
    ) {
      localStorage.clear();
      window.location.replace('/login');
      return result;
    }

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const expDate = new Date(expNumber * 1000);
        const hasExpired = isPast(expDate);
        if (exp && refreshToken && hasExpired) {
          // REFRESH TOKEN
          const { generatedCas } = await import('./generated-cas');
          const refreshResponse = await api.dispatch(generatedCas.endpoints.postApiAuthRefreshToken.initiate());

          if (!refreshResponse.error) {
            // re-run the request
            result = await dynamicBaseQuery(args, api, extraOptions);
          }
        } else if (!window.location.pathname.endsWith('/login')) {
          // The access token has not expired, yet the server rejected it: the session was
          // revoked (deactivation, password change/reset, replay detection). Permission
          // failures are 403, so a 401 here is unambiguous. A refresh would fail the same
          // way, so end the session instead of returning the 401 to the screen and leaving
          // the app visually signed in with every request failing.
          localStorage.clear();
          window.location.replace('/login');
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await dynamicBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};
